import React from 'react';
import { Box, Button } from '@mui/material';
import { APP_CONFIG } from '../../../config/app.config';

interface OnboardingPagerFooterProps {
  stepCount: number;
  activeIndex: number;
  onNext: () => void;
  nextLabel: string;
  doneLabel?: string;
  isLast?: boolean;
  onDotClick?: (index: number) => void;
}

const OnboardingPagerFooter: React.FC<OnboardingPagerFooterProps> = ({
  stepCount,
  activeIndex,
  onNext,
  nextLabel,
  doneLabel = 'Done',
  isLast = activeIndex >= stepCount - 1,
  onDotClick,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const label = isLast ? doneLabel : nextLabel;
  // Keep button width stable when label swaps (e.g. Next → View products).
  const widthAnchor =
    nextLabel.length >= doneLabel.length ? nextLabel : doneLabel;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
      }}
    >
      <Box />

      <Box
        sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}
        role="tablist"
        aria-label="Onboarding steps"
      >
        {Array.from({ length: stepCount }).map((_, index) => {
          const selected = index === activeIndex;
          return (
            <Box
              key={index}
              component={onDotClick ? 'button' : 'span'}
              type={onDotClick ? 'button' : undefined}
              aria-label={`Step ${index + 1}`}
              aria-current={selected ? 'step' : undefined}
              onClick={onDotClick ? () => onDotClick(index) : undefined}
              sx={{
                width: 8,
                height: 8,
                p: 0,
                border: 0,
                borderRadius: '50%',
                bgcolor: selected ? colors.primary : colors.border,
                cursor: onDotClick ? 'pointer' : 'default',
                transition: 'background-color 160ms ease',
              }}
            />
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={onNext}>
          <Box
            component="span"
            sx={{ display: 'inline-grid', justifyItems: 'center' }}
          >
            <Box
              component="span"
              sx={{ gridArea: '1 / 1', visibility: 'hidden' }}
              aria-hidden
            >
              {widthAnchor}
            </Box>
            <Box component="span" sx={{ gridArea: '1 / 1' }}>
              {label}
            </Box>
          </Box>
        </Button>
      </Box>
    </Box>
  );
};

export default OnboardingPagerFooter;
